/** Protocol adapter: form mutations and selection stay outside the shared editor. */
import { CollisionPriority } from "@dnd-kit/abstract";
import { RestrictToVerticalAxis } from "@dnd-kit/abstract/modifiers";
import { DragDropProvider, KeyboardSensor, PointerSensor, useDraggable, useDroppable } from "@dnd-kit/react";
import { Badge } from "@repo/ui/components/ui/badge";
import { Button } from "@repo/ui/components/ui/button";
import { cn } from "@repo/ui/lib/utils";
import { useStore } from "@tanstack/react-form";
import { Layers, Trash2 } from "lucide-react";
import { useState } from "react";
import {
  type ActionFormValues,
  createDefaultStep,
  EMPTY_DEFAULTS,
  nextStepOffset,
  resolveBuilderStep,
  type StepFormValues,
  withForm,
} from "@/features/catalog/forms/use-plan-definition-form";
import { AddCatalogItemButton } from "@/shared/common/add-catalog-item-button";
import { qualifiedProcedureTitle } from "@/shared/lib/procedure-title";
import {
  EditableTimelineProvider,
  type EditableTimelineStep,
  TimelineEditor,
  TimelineProcedureRow,
} from "@/shared/timeline";
import { ProcedureCard, resolveProcedureCardTitle } from "./procedure-card";
import { findProtocolBuilderProcedure, type ProtocolBuilderProcedure } from "./protocol-builder-definition";
import {
  addProcedureToStep,
  applyTimelinePlacements,
  removeBuilderStep,
  splitProcedureFromStep,
} from "./step-operations";

const cardSensors = [
  PointerSensor.configure({
    activatorElements(source) {
      return source.handle ? [source.handle] : [source.element];
    },
  }),
  KeyboardSensor,
];

export interface StepBuilderSectionProps {
  selectedActionId: string | null;
  onSelectAction: (actionId: string, stepIdx: number) => void;
  onAddProcedureToStep: (stepIdx: number) => void;
  activityDefinitions?: ProtocolBuilderProcedure[];
}
export const StepBuilderSection = withForm({
  defaultValues: EMPTY_DEFAULTS,
  props: {} as StepBuilderSectionProps,
  render: function Render({ form, selectedActionId, onSelectAction, onAddProcedureToStep, activityDefinitions = [] }) {
    const steps = useStore(form.store, (s) => s.values.steps);
    const exams = useStore(form.store, (s) => s.values.exams);
    const envelopeMinutes = useStore(form.store, (s) => s.values.envelopeMinutes);
    const [selectedStepId, setSelectedStepId] = useState<string | null>(
      () => steps.find((step) => step.actions.some((action) => action.id === selectedActionId))?.id ?? null,
    );
    const [placementError, setPlacementError] = useState<string>();
    const timeline = projectTimeline(steps, exams, activityDefinitions);
    const change = (operation: () => StepFormValues[]) => {
      try {
        form.setFieldValue("steps", operation());
        setPlacementError(undefined);
      } catch (cause) {
        setPlacementError(cause instanceof Error ? cause.message : "Could not update step");
      }
    };
    const removeStep = (index: number) => {
      const next = removeBuilderStep(steps, index, envelopeMinutes);
      form.setFieldValue("steps", next.steps);
      form.setFieldValue("envelopeMinutes", next.envelopeMinutes);
      if (selectedStepId === steps[index]?.id) setSelectedStepId(null);
    };
    const removeProcedure = (stepIdx: number, actionId: string) => {
      const step = steps[stepIdx];
      if (!step) return;
      if (step.actions.length === 1) {
        removeStep(stepIdx);
        return;
      }
      form.setFieldValue(
        "steps",
        steps.map((item, i) =>
          i === stepIdx ? { ...item, actions: item.actions.filter((a) => a.id !== actionId) } : item,
        ),
      );
    };
    const moveProcedure = (id: string, targetId: string) =>
      change(() => {
        const source = steps.find((step) => step.actions.some((action) => action.id === id));
        const action = source?.actions.find((action) => action.id === id);
        if (!source || !action || source.id === targetId || !steps.some((step) => step.id === targetId)) return steps;
        const vacated = source.actions.length === 1;
        const released = vacated
          ? removeBuilderStep(steps, steps.indexOf(source), envelopeMinutes)
          : {
              steps: steps.map((step) =>
                step.id === source.id ? { ...step, actions: step.actions.filter((item) => item.id !== id) } : step,
              ),
              envelopeMinutes,
            };
        const next = addProcedureToStep(
          released.steps,
          released.steps.findIndex((step) => step.id === targetId),
          action,
        );
        form.setFieldValue(
          "envelopeMinutes",
          Math.max(
            released.envelopeMinutes,
            ...next.flatMap((step) => (step.actions.length ? [resolveBuilderStep(step).endMinutes] : [])),
          ),
        );
        if (vacated && selectedStepId === source.id) setSelectedStepId(targetId);
        return next;
      });
    const renderProcedures = (stepId: string) => {
      const stepIdx = steps.findIndex((step) => step.id === stepId);
      const step = steps[stepIdx];
      if (!step) return null;
      const included = step.actions;
      return (
        <fieldset aria-label={`Step ${stepIdx + 1} procedures`} className="min-w-0 space-y-0.5">
          {step.actions.map((action, index) => (
            <TimelineProcedureRow
              key={action.id}
              procedure={{
                id: action.id,
                title: qualifiedProcedureTitle(
                  resolveProcedureCardTitle(
                    action.title,
                    findProtocolBuilderProcedure(action.definitionCanonical, activityDefinitions),
                    index,
                  ),
                  action.qualifier,
                ),
              }}
              stepId={step.id}
              editable
              selected={selectedActionId === action.id}
              onSelect={() => {
                setSelectedStepId(step.id);
                onSelectAction(action.id, stepIdx);
              }}
              onSplit={
                included.length > 1 ? () => change(() => splitProcedureFromStep(steps, stepIdx, index)) : undefined
              }
              onRemove={() => removeProcedure(stepIdx, action.id)}
            />
          ))}
        </fieldset>
      );
    };
    return (
      <div className="space-y-3">
        {timeline.error ? (
          <p role="alert" className="text-sm text-destructive">
            {timeline.error}
          </p>
        ) : (
          <EditableTimelineProvider
            steps={timeline.steps}
            envelopeMinutes={envelopeMinutes}
            density="editor"
            editable
            selectedId={selectedStepId}
            onSelectionChange={setSelectedStepId}
            onStepsChange={({ steps: updated }) =>
              form.setFieldValue("steps", applyTimelinePlacements(steps, timeline.steps, updated))
            }
            onEnvelopeChange={(minutes) => form.setFieldValue("envelopeMinutes", minutes)}
            onAddProcedure={(stepId) =>
              onAddProcedureToStep(stepId ? steps.findIndex((step) => step.id === stepId) : -1)
            }
            onMoveProcedure={moveProcedure}
          >
            <TimelineEditor
              renderStepProcedures={renderProcedures}
              onReset={() => form.reset()}
              resetLabel="Reset"
              resetTitle="Discard every change back to the saved protocol"
              addLabel="Add procedure"
            />
          </EditableTimelineProvider>
        )}
        {steps.length > 0 && (
          <DragDropProvider
            sensors={cardSensors}
            // biome-ignore lint/suspicious/noExplicitAny: dnd-kit event types
            onDragEnd={(event: any) => {
              const { source, target } = event.operation;
              if (event.canceled || source?.type !== "procedure" || target?.type !== "step") return;
              moveProcedure(String(source.id), String(target.id));
            }}
          >
            <div className="space-y-2">
              {steps.map((step, stepIdx) => {
                const included = step.actions;
                return (
                  <StepCard
                    key={step.id}
                    step={step}
                    stepIndex={stepIdx}
                    selected={selectedStepId === step.id}
                    onSelect={() => setSelectedStepId(step.id)}
                  >
                    <div className="flex h-10 items-center justify-between rounded-t-lg border-b bg-muted/30 px-3">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="text-sm font-medium"
                          aria-label={`Select Step ${stepIdx + 1}`}
                          aria-pressed={selectedStepId === step.id}
                          onClick={() => setSelectedStepId(step.id)}
                        >
                          Step {stepIdx + 1}
                        </button>
                        {included.length > 1 && (
                          <Badge variant="secondary" className="gap-1 text-xs font-normal">
                            <Layers className="size-2.5" />
                            Concurrent
                          </Badge>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Remove step"
                        className="size-6 text-muted-foreground hover:text-muted-foreground"
                        type="button"
                        onClick={() => removeStep(stepIdx)}
                      >
                        <Trash2 className="size-3" />
                      </Button>
                    </div>
                    <div className="space-y-1.5 p-2">
                      {step.actions.map((action, procIdx) => (
                        <DraggableProcedureCard
                          key={action.id}
                          action={action}
                          procIndex={procIdx}
                          isSelected={selectedActionId === action.id}
                          onSelect={() => {
                            setSelectedStepId(step.id);
                            onSelectAction(action.id, stepIdx);
                          }}
                          onSplit={
                            included.length > 1
                              ? () => change(() => splitProcedureFromStep(steps, stepIdx, procIdx))
                              : undefined
                          }
                          onRemove={() => removeProcedure(stepIdx, action.id)}
                          activityDefinitions={activityDefinitions}
                        />
                      ))}
                    </div>
                    <div className="px-2 pb-2">
                      <AddCatalogItemButton label="Procedure" onClick={() => onAddProcedureToStep(stepIdx)} />
                    </div>
                  </StepCard>
                );
              })}
            </div>
          </DragDropProvider>
        )}
        {placementError && (
          <p role="alert" className="text-sm text-destructive">
            {placementError}
          </p>
        )}
        <AddCatalogItemButton
          label="Step"
          onClick={() => change(() => [...steps, createDefaultStep(nextStepOffset(form.state.values))])}
        />
      </div>
    );
  },
});
/** Empty cards remain drop targets, so a procedure can move into an untimed step. */
function StepCard({
  step,
  stepIndex,
  selected,
  onSelect,
  children,
}: {
  step: StepFormValues;
  stepIndex: number;
  selected: boolean;
  onSelect: () => void;
  children: React.ReactNode;
}) {
  const { ref } = useDroppable({
    id: step.id,
    type: "step",
    accept: "procedure",
    collisionPriority: CollisionPriority.Low,
  });
  return (
    <section
      ref={ref}
      aria-label={`Step ${stepIndex + 1} card`}
      data-selected={selected}
      onClickCapture={onSelect}
      onFocusCapture={onSelect}
      className={cn("rounded-lg border", selected && "ring-2 ring-primary")}
    >
      {children}
    </section>
  );
}

function DraggableProcedureCard({
  action,
  procIndex,
  isSelected,
  onSelect,
  onSplit,
  onRemove,
  requiredByExamNames,
  activityDefinitions,
}: {
  action: ActionFormValues;
  procIndex: number;
  isSelected: boolean;
  onSelect: () => void;
  onSplit?: () => void;
  onRemove: () => void;
  requiredByExamNames?: string[];
  activityDefinitions: ProtocolBuilderProcedure[];
}) {
  const { ref, handleRef, isDragging } = useDraggable({
    id: action.id,
    type: "procedure",
    modifiers: [RestrictToVerticalAxis],
  });
  return (
    <div ref={ref} className={cn("space-y-0.5", isDragging && "opacity-50")}>
      <ProcedureCard
        action={action}
        index={procIndex}
        isSelected={isSelected}
        onSelect={onSelect}
        onDuplicate={() => {}}
        onRemove={onRemove}
        onSplit={onSplit}
        requiredByExamNames={requiredByExamNames}
        dragHandleProps={{ ref: handleRef as (node: HTMLElement | null) => void }}
        activityDefinitions={activityDefinitions}
        showDescription={false}
      />
    </div>
  );
}

/** Incomplete procedure drafts are never coerced into timed placements. */
function projectTimeline(
  steps: StepFormValues[],
  _exams: { canonical: string; title: string }[],
  definitions: ProtocolBuilderProcedure[],
): { steps: EditableTimelineStep[]; error?: string } {
  try {
    return {
      steps: steps.flatMap((step, index) => {
        const actions = step.actions;
        if (!actions.length) return [];
        const placement = resolveBuilderStep({ ...step, actions });
        const procedures = actions.map((action, procIndex) => ({
          id: action.id,
          title: qualifiedProcedureTitle(
            resolveProcedureCardTitle(
              action.title,
              findProtocolBuilderProcedure(action.definitionCanonical, definitions),
              procIndex,
            ),
            action.qualifier,
          ),
        }));

        return [
          {
            id: step.id,
            title: `Step ${index + 1}`,
            placement: { offsetMinutes: placement.offsetMinutes, durationMinutes: placement.durationMinutes },
            procedures,
          },
        ];
      }),
    };
  } catch (cause) {
    return {
      steps: [],
      error: `Timeline unavailable: ${cause instanceof Error ? cause.message : "Check step placements."}`,
    };
  }
}
